import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// Attach Bearer token from localStorage as fallback for cookie auth
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Helper to add analysis_id
const aq = (analysisId) => analysisId ? `?analysis_id=${analysisId}` : "";

// Analyses
export const getAnalyses = () => api.get("/analyses");
export const createAnalysis = (data) => api.post("/analyses", data);
export const updateAnalysis = (id, data) => api.put(`/analyses/${id}`, data);
export const deleteAnalysis = (id) => api.delete(`/analyses/${id}`);
export const duplicateAnalysis = (id) => api.post(`/analyses/${id}/duplicate`);

// Transactions
export const getTransactions = (aId) => api.get(`/transactions${aq(aId)}`);
export const addManualTransaction = (data, aId) => api.post(`/transactions/manual${aq(aId)}`, data);
export const uploadCSV = (file, aId) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/transactions/upload-csv${aq(aId)}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const uploadPDF = (file, aId) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/transactions/upload-pdf${aq(aId)}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};
export const loadSampleTransactions = (aId) => api.post(`/transactions/load-sample${aq(aId)}`);
export const updateTransaction = (id, data) => api.put(`/transactions/${id}`, data);
export const deleteTransaction = (id) => api.delete(`/transactions/${id}`);
export const clearTransactions = (aId) => api.delete(`/transactions${aq(aId)}`);

// Cash Flow
export const getCashFlowReport = (aId) => api.get(`/cash-flow-report${aq(aId)}`);

// Loan Offers
export const getLoanOffers = (aId) => api.get(`/loan-offers${aq(aId)}`);
export const createLoanOffer = (data, aId) => api.post(`/loan-offers${aq(aId)}`, data);
export const updateLoanOffer = (id, data) => api.put(`/loan-offers/${id}`, data);
export const deleteLoanOffer = (id) => api.delete(`/loan-offers/${id}`);
export const loadSampleOffers = (aId) => api.post(`/loan-offers/load-sample${aq(aId)}`);

// Comparison & Recommendation
export const getLoanComparison = (aId) => api.get(`/loan-comparison${aq(aId)}`);
export const getFinalRecommendation = (aId) => api.get(`/final-recommendation${aq(aId)}`);

// PDF Reports (with template)
export const downloadPdfUrl = (type, aId, template = "detailed") =>
  `${API}/reports/${type}-pdf${aq(aId)}${aId ? "&" : "?"}template=${template}`;

// Share
export const createShareLink = (data, aId) => api.post(`/share-links${aq(aId)}`, data);
export const getSharedData = (linkId) => api.get(`/shared/${linkId}`);
export const shareViaEmail = (data) => api.post("/share/email", data);

// AI Offer Parsing
export const parseOffersFromText = (text, aId) => api.post(`/loan-offers/parse-text${aq(aId)}`, { text });

// Bulk PDF Upload
export const uploadBulkPDFs = (files, aId) => {
  const formData = new FormData();
  files.forEach((f) => formData.append("files", f));
  return api.post(`/transactions/upload-pdfs${aq(aId)}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Historical Comparison
export const compareAnalyses = (ids) => api.post("/analyses/compare", { analysis_ids: ids });

// Global Demo
export const loadAllDemoData = (aId) => api.post(`/demo/load-all${aq(aId)}`);

// CSV Export
export const exportCsvUrl = (type, aId) => `${API}/export/${type}-csv${aq(aId)}`;

// Notifications
export const getNotifications = () => api.get("/notifications");
export const markNotificationsRead = () => api.post("/notifications/mark-read");

// Admin
export const getAdminStats = () => api.get("/admin/stats");
export const getAdminUsers = () => api.get("/admin/users");

// User Profile
export const uploadLogo = (logo_base64) => api.post("/user/logo", { logo_base64 });
export const getLogo = () => api.get("/user/logo");
export const verifyPinApi = (pin) => api.post("/auth/verify-pin", { pin });
export const changePinApi = (current_pin, new_pin) => api.post("/auth/change-pin", { current_pin, new_pin });

// Admin CRM
export const getAdminDeals = () => api.get("/admin/deals");
export const getAdminDealDetail = (id) => api.get(`/admin/deals/${id}`);

// Full Report
export const fullReportPdfUrl = (aId) => `${API}/reports/full-pdf${aq(aId)}`;

export const getCategories = () => api.get("/categories");
